import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import { 
  Bell, CheckCircle, Package, FileText, User, Wrench, 
  Calendar as CalendarIcon, RefreshCw, X, Download,
  FileSpreadsheet
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function Notifications({ user, onLogout }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters - default to 'today'
  const [periodFilter, setPeriodFilter] = useState('today');
  const [specificDate, setSpecificDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (specificDate) {
      fetchNotificationsByDate(specificDate);
    } else {
      fetchNotifications(periodFilter);
    }
  }, [periodFilter, specificDate]);

  const fetchNotifications = async (period) => {
    try {
      setLoading(true);
      const response = await api.get(`/notifications?period=${period}`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationsByDate = async (date) => {
    try {
      setLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/notifications?specific_date=${dateStr}`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSpecificDate(date);
    setPeriodFilter('');
    setCalendarOpen(false);
  };

  const clearSpecificDate = () => {
    setSpecificDate(null);
    setPeriodFilter('today');
  };

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    setSpecificDate(null);
  };

  const handleExport = async (period, format) => {
    try {
      setExporting(true);
      const exportPeriod = specificDate ? 'today' : (period || periodFilter);
      
      if (format === 'csv') {
        const response = await api.get(`/notifications/export?period=${exportPeriod}&format=csv`);
        const data = response.data;
        
        const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        toast.success(`Exported ${data.total_notifications} notifications to CSV`);
      } else if (format === 'pdf') {
        const response = await api.get(`/notifications/export?period=${exportPeriod}&format=json`);
        const data = response.data;
        
        generatePDF(data);
        toast.success(`Generated PDF report with ${data.total_notifications} notifications`);
      }
    } catch (error) {
      toast.error('Failed to export notifications');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const generatePDF = (data) => {
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${data.period_label}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
          h1 { color: #1e3a5f; font-size: 18px; margin-bottom: 5px; }
          .meta { color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background-color: #1e3a5f; color: white; padding: 8px; text-align: left; font-size: 11px; }
          td { border: 1px solid #ddd; padding: 6px; font-size: 10px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .unread { background-color: #e3f2fd; }
          .read { color: green; }
          @media print {
            body { margin: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${data.period_label}</h1>
        <div class="meta">
          Generated: ${data.generated_at} | Total Notifications: ${data.total_notifications}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Type</th>
              <th>Title</th>
              <th>Message</th>
              <th>Created By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${data.data.map(row => `
              <tr class="${row.status === 'Unread' ? 'unread' : ''}">
                <td>${row.date}</td>
                <td>${row.time}</td>
                <td>${row.type}</td>
                <td>${row.title}</td>
                <td>${row.message}</td>
                <td>${row.created_by}</td>
                <td class="${row.status === 'Read' ? 'read' : ''}">${row.status}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/mark-read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    if (notification.reference_type === 'delivery' && notification.reference_id) {
      navigate(`/deliveries/${notification.reference_id}`);
    } else if (notification.reference_type === 'customer') {
      navigate('/pallet-statement');
    } else if (notification.reference_type === 'vehicle') {
      navigate('/vehicles');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'compliance_supervisor':
      case 'compliance_driver':
        return <FileText className="h-5 w-5 text-blue-600" />;
      case 'pallet_out':
      case 'pallet_in':
        return <Package className="h-5 w-5 text-green-600" />;
      case 'vehicle_maintenance':
        return <Wrench className="h-5 w-5 text-orange-600" />;
      case 'backup_expiry_warning':
        return <Bell className="h-5 w-5 text-amber-600" />;
      case 'driver_reassignment':
        return <RefreshCw className="h-5 w-5 text-indigo-600" />;
      default:
        return <Bell className="h-5 w-5 text-slate-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'compliance_supervisor':
      case 'compliance_driver':
        return 'border-l-blue-500';
      case 'pallet_out':
        return 'border-l-orange-500';
      case 'pallet_in':
        return 'border-l-green-500';
      case 'vehicle_maintenance':
        return 'border-l-red-500';
      case 'backup_expiry_warning':
        return 'border-l-amber-500';
      case 'driver_reassignment':
        return 'border-l-indigo-500';
      default:
        return 'border-l-slate-500';
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const getPageTitle = () => {
    if (specificDate) {
      return `Notifications for ${specificDate.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })}`;
    }
    switch (periodFilter) {
      case 'today': return "Today's Notifications";
      case 'week': return "Last 7 Days";
      case 'month': return "Last 30 Days";
      case 'year': return "Last Year";
      case 'all': return "All Notifications";
      default: return "Notifications";
    }
  };

  const formatDateForButton = (date) => {
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{getPageTitle()}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              {loading ? 'Loading...' : `${notifications.length} notifications found${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline" size="sm">
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark All Read
              </Button>
            )}
            <Button
              onClick={() => specificDate ? fetchNotificationsByDate(specificDate) : fetchNotifications(periodFilter)}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={exporting || notifications.length === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport(periodFilter, 'csv')}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport(periodFilter, 'pdf')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filter Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Filter by Date
              </CardTitle>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {/* Period Dropdown */}
                <Select value={specificDate ? '' : periodFilter} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today (Default)</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                    <SelectItem value="year">Last Year</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>

                {/* Calendar Date Picker */}
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={specificDate ? "default" : "outline"}
                      className={`w-full sm:w-auto justify-start text-left font-normal ${
                        specificDate ? 'bg-blue-600 text-white hover:bg-blue-700' : ''
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {specificDate ? formatDateForButton(specificDate) : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={specificDate}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* Clear Date Button */}
                {specificDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSpecificDate}
                    className="text-slate-600 hover:text-slate-800"
                    title="Clear date filter"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Notifications List */}
        {loading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mb-4" />
                <p className="text-slate-600">Loading notifications...</p>
              </div>
            </CardContent>
          </Card>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Notifications</h3>
              <p className="text-slate-600">
                {specificDate 
                  ? `No notifications for ${specificDate.toLocaleDateString('en-GB')}. Try a different date.`
                  : periodFilter === 'today'
                    ? "No notifications for today. Try selecting a different period."
                    : "You're all caught up! Notifications will appear here."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${getNotificationColor(notification.notification_type)} ${
                  notification.is_read ? 'bg-white' : 'bg-blue-50'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`text-sm font-semibold ${notification.is_read ? 'text-slate-900' : 'text-blue-900'}`}>
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{notification.message}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{new Date(notification.created_at).toLocaleDateString('en-GB')}</span>
                        <span>•</span>
                        <span>{new Date(notification.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                        {notification.created_by_name && (
                          <>
                            <span>•</span>
                            <span className="text-blue-600 flex items-center gap-1">
                              <User className="h-3 w-3" /> {notification.created_by_name}
                            </span>
                          </>
                        )}
                        {notification.is_read && notification.read_at && (
                          <>
                            <span>•</span>
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" /> Read
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
